import { useCallback } from 'react'
import * as R from 'ramda'
import type { Nil } from '@pbt/pbt-ui-components'

import {
  type GeneratedMessageAttachmentsSubsection,
  type GeneratedMessageAttachmentSubcategory,
  type GeneratedMessageSectionAttachmentsInput,
  type MedicalHistoryAttachmentsSection,
  type MedicalHistoryBaseSection,
  type MedicalHistoryConfig,
  type MedicalHistorySection,
  type MedicalHistorySectionInput,
  MedicalHistorySectionType,
  type MedicalHistorySoapPreviewInput,
} from '~/api/graphql/generated/types'
import {
  useMedicalHistoryAttachmentsSectionsLazyQuery,
  useMedicalHistoryImagingAttachmentsSectionsLazyQuery,
  useMedicalHistoryLabAttachmentsSectionsLazyQuery,
} from '~/api/graphql/queries/medicalHistoryAttachmentsSections'
import i18nPortal from '~/locales/i18n'
import type { EmailEntityConfig, EmailEntityConfigSection } from '~/types'

import { removePropsRecursively } from '.'

export function hasGrandChildWithId(
  child: EmailEntityConfigSection,
  id: string,
): boolean {
  return child.children?.some((grandChild) => grandChild.id === id) ?? false
}

export function isChildOrGrandChildWithId(
  child: EmailEntityConfigSection,
  id: string,
): boolean {
  return child.id === id || hasGrandChildWithId(child, id)
}

export const updateChecked = (
  id: string,
  sections: EmailEntityConfigSection[] | Nil,
  checked: boolean,
  forceUpdate = false,
): EmailEntityConfigSection[] => {
  const newSections = (sections || []).map((item: EmailEntityConfigSection) => {
    const newChildren = item.children
      ? updateChecked(id, item.children, checked, forceUpdate || item.id === id)
      : item.children

    const hasCheckedChildren = newChildren
      ? newChildren.some((child: EmailEntityConfigSection) => child.checked)
      : false

    const hasAllChildrenUnchecked = newChildren
      ? newChildren.every((child: EmailEntityConfigSection) => !child.checked)
      : false

    const isCurrentSection = item.id === id

    function shouldUncheckAllChildrenAndSection(): boolean {
      if (
        hasAllChildrenUnchecked &&
        checked === false &&
        newChildren?.some((child) => isChildOrGrandChildWithId(child, id))
      ) {
        return true
      }
      return false
    }

    const getNewChecked = () => {
      if (forceUpdate || isCurrentSection) {
        return checked
      }

      if (newChildren?.length) {
        // Only if all children are unchecked and the current item is being unchecked and it is part of the children
        if (shouldUncheckAllChildrenAndSection()) {
          return false
        }
        return hasCheckedChildren || item.checked
      }

      return item.checked
    }

    return {
      ...item,
      checked: getNewChecked(),
      children: newChildren,
    }
  })

  return newSections
}

const sectionTooltipMap: Record<MedicalHistorySectionType, string> = {
  LABS: i18nPortal.t('Tooltips:MEDICAL_HISTORY.LABS_SECTION_TOOLTIP'),
  ATTACHMENTS: i18nPortal.t(
    'Tooltips:MEDICAL_HISTORY.ATTACHMENTS_SECTION_TOOLTIP',
  ),
  IMAGING: i18nPortal.t('Tooltips:MEDICAL_HISTORY.IMAGING_SECTION_TOOLTIP'),
  ALERTS: '',
  APPOINTMENTS: '',
  COMMUNICATIONS: '',
  DIAGNOSES: '',
  IMPORTED_HISTORY: '',
  OTC_SALE: '',
  PRESCRIPTIONS_DRUGS: '',
  QUESTIONS: '',
  REMINDERS: '',
  SURGERY: '',
  VACCINATIONS: '',
  NOTES: '',
  VITALS: '',
}

// Type guard to check if section has attachments
export const hasAttachments = (
  section: MedicalHistorySection,
): section is MedicalHistoryAttachmentsSection => 'attachments' in section

export const normalizeSection = (
  section: MedicalHistorySection,
): EmailEntityConfigSection => {
  const tooltip = sectionTooltipMap[section.section]
  const isAttachmentsSection =
    section.section === MedicalHistorySectionType.Attachments

  return {
    ...R.omit(['attachments'], section),
    tooltip,
    defaultExpanded: !isAttachmentsSection,
    children: hasAttachments(section)
      ? section.attachments?.map((child) => {
          const normalizedName =
            child.addedDate &&
            // Attachments section already has "Added" in the name as per BE implementation
            !isAttachmentsSection
              ? `${child.name} | ${i18nPortal.t('Common:ADDED')}`
              : child.name

          return {
            ...child,
            hideSubcategory: true,
            name: normalizedName,
            children: child.children?.map(
              (grandChild: GeneratedMessageAttachmentsSubsection) => ({
                ...grandChild,
                unselectable: true,
              }),
            ),
          }
        })
      : undefined,
  }
}

export const mergeMedicalHistoryConfigs = ({
  sections,
  config,
}: {
  config: EmailEntityConfig | undefined
  sections:
    | {
        attachments: MedicalHistorySection | null | undefined
        imaging: MedicalHistorySection | null | undefined
        labs: MedicalHistorySection | null | undefined
      }
    | undefined
}): EmailEntityConfig | undefined => {
  if (!config) {
    return undefined
  }

  const {
    attachments: attachmentSection,
    labs: labsSection,
    imaging: imagingSection,
  } = sections || {}

  // Collect sections to merge
  const sectionsToMerge: MedicalHistorySection[] = [
    attachmentSection,
    labsSection,
    imagingSection,
  ].filter(
    (section): section is MedicalHistorySection =>
      section !== null && section !== undefined,
  )

  if (sectionsToMerge.length === 0) {
    return config
  }

  // Create a map of normalized sections by type
  const normalizedSectionsMap = new Map(
    sectionsToMerge.map((section) => [
      section.section,
      normalizeSection(section),
    ]),
  )

  // Replace sections while preserving order
  const mergedSections =
    config.sections?.map((s) => {
      if (
        s &&
        'section' in s &&
        normalizedSectionsMap.has(s.section as MedicalHistorySectionType)
      ) {
        return normalizedSectionsMap.get(s.section as MedicalHistorySectionType)
      }
      return s
    }) || []

  return {
    ...config,
    sections: mergedSections as EmailEntityConfigSection[],
  }
}

export const convertGraphqlToMedicalHistoryConfig = (
  queryConfigData: MedicalHistoryConfig | undefined,
): EmailEntityConfig | undefined => {
  if (!queryConfigData) {
    return undefined
  }

  return {
    message: queryConfigData?.message,
    subject: queryConfigData?.subject,
    sections: queryConfigData?.sections?.map(
      (
        section: MedicalHistoryAttachmentsSection | MedicalHistoryBaseSection,
      ) => {
        const tooltip = sectionTooltipMap[section.section]

        if (
          section.__typename === 'MedicalHistoryAttachmentsSection' &&
          'children' in section &&
          Array.isArray(section.children) &&
          // We are only normalizing the LABS and IMAGING sections as aligned with BE.
          // Like normalizing the added date, today the Attachments section returns the Added in the name
          // and we will remove in the future as debt tech
          ['IMAGING', 'LABS'].includes(section.section)
        ) {
          return {
            ...section,
            tooltip,
            defaultExpanded: true,
            children: section.children.map((child) => {
              const normalizedName = child.addedDate
                ? `${child.name} | ${i18nPortal.t('Common:ADDED')}`
                : child.name

              return {
                ...child,
                // We don't want to display the subcategory for this section
                hideSubcategory: true,
                name: normalizedName,
                children: child.children?.map(
                  (grandChild: GeneratedMessageAttachmentsSubsection) => ({
                    ...grandChild,
                    // Only display items as text
                    unselectable: true,
                  }),
                ),
              }
            }),
          }
        }

        return { ...section, tooltip }
      },
    ),
  }
}

// This query is used to fetch the attachments sections (attachments, labs, imaging) when the feature toggle is enabled
// We decided to split the attachments to improve the performance on BE side.
// But we still need to merge them back to the config when rendering the dialog
export const useQueryAttachmentsSections = () => {
  const [
    queryAttachmentsSections,
    {
      data: attachmentsResponse,
      loading: attachmentsSectionsQueryIsLoading,
      error: attachmentsSectionsError,
    },
  ] = useMedicalHistoryAttachmentsSectionsLazyQuery()
  const [
    queryLabAttachmentsSections,
    {
      data: labAttachmentsResponse,
      loading: labAttachmentsSectionsQueryIsLoading,
      error: labAttachmentsSectionsError,
    },
  ] = useMedicalHistoryLabAttachmentsSectionsLazyQuery()
  const [
    queryImagingAttachmentsSections,
    {
      data: imagingAttachmentsResponse,
      loading: imagingAttachmentsSectionsQueryIsLoading,
      error: imagingAttachmentsSectionsError,
    },
  ] = useMedicalHistoryImagingAttachmentsSectionsLazyQuery()

  const attachmentsData = attachmentsResponse?.attachments
  const labAttachmentsData = labAttachmentsResponse?.labs
  const imagingAttachmentsData = imagingAttachmentsResponse?.imaging

  const isLoadingAttachmentsSections =
    attachmentsSectionsQueryIsLoading ||
    labAttachmentsSectionsQueryIsLoading ||
    imagingAttachmentsSectionsQueryIsLoading
  const hasAttachmentsError = Boolean(attachmentsSectionsError)
  const hasLabAttachmentsError = Boolean(labAttachmentsSectionsError)
  const hasImagingAttachmentsError = Boolean(imagingAttachmentsSectionsError)
  const hasAnyAttachmentsError =
    hasAttachmentsError || hasLabAttachmentsError || hasImagingAttachmentsError

  const fetchAllAttachmentsSections = async ({
    patientId,
    from,
    to,
  }: {
    from?: string
    patientId: string
    to?: string
  }) => {
    await Promise.all([
      queryAttachmentsSections({
        variables: { patientId, from, to },
      }),
      queryLabAttachmentsSections({
        variables: { patientId, from, to },
      }),
      queryImagingAttachmentsSections({
        variables: { patientId, from, to },
      }),
    ])
  }

  const getHasAttachmentsErrorForSection = useCallback(
    (section: string | undefined) => {
      const isAttachmentSection =
        section === MedicalHistorySectionType.Attachments
      const isLabsSection = section === MedicalHistorySectionType.Labs
      const isImagingSection = section === MedicalHistorySectionType.Imaging

      const hasError =
        (hasAttachmentsError && isAttachmentSection) ||
        (hasLabAttachmentsError && isLabsSection) ||
        (hasImagingAttachmentsError && isImagingSection)

      return hasError
    },
    [hasAttachmentsError, hasImagingAttachmentsError, hasLabAttachmentsError],
  )

  return {
    attachmentsData,
    labAttachmentsData,
    imagingAttachmentsData,
    getHasAttachmentsErrorForSection,
    hasAnyAttachmentsError,
    isLoadingAttachmentsSections,
    fetchAllAttachmentsSections,
  }
}

export const collectAttachmentsRecursivelyFromChildren = (
  nodes?: EmailEntityConfigSection[] | null,
): GeneratedMessageSectionAttachmentsInput[] =>
  R.reduce<EmailEntityConfigSection, GeneratedMessageSectionAttachmentsInput[]>(
    (acc, node) => {
      const current: GeneratedMessageSectionAttachmentsInput = {
        id: node.id,
        subcategory: node.subcategory as GeneratedMessageAttachmentSubcategory,
      }
      const kids = collectAttachmentsRecursivelyFromChildren(
        node.children?.filter((it) => it.checked),
      )
      return R.concat(acc, R.prepend(current, kids))
    },
    [],
    nodes ?? [],
  )

export const convertMedicalHistoryConfigToPreviewInput = (
  config: EmailEntityConfig,
  patientId: string,
): MedicalHistorySoapPreviewInput => ({
  patientId: config.patientId || patientId,
  message: config.message || null,
  subject: config.subject || '',
  from: config.from,
  to: config.to,
  sections: config?.sections
    ?.filter((section) => section?.checked)
    ?.map(
      (section: any) =>
        ({
          section: (section as MedicalHistorySection)?.section,
          attachments: collectAttachmentsRecursivelyFromChildren(
            (section as EmailEntityConfigSection)?.children?.filter(
              (it) => it.checked,
            ),
          ),
        }) as MedicalHistorySectionInput,
    ),
})

export const normalizeSections = (
  sections: (EmailEntityConfigSection | null)[] | Nil,
) =>
  removePropsRecursively([
    '__typename',
    'hideSubcategory',
    'unselectable',
    'section',
    'defaultExpanded',
    'tooltip',
    'hasError',
  ])(sections)
